import { TaJson } from "ta-json";

import Guard from "../guard";
import { AllFacetValuesResponse } from "../models/search/all-facet-values-response";
import { AllFacetsRequest } from "../models/search/all-facets-request";
import { SearchRequest } from "../models/search/search-request";
import { SearchResponse } from "../models/search/search-response";
import { IExtendedContentHubClient } from "./extended-client";
import { CancelCallback } from "./internal-client";
import { ResponseHandler } from "./response-handler";

export interface ISearchClient {
    /**
     * Send a {@link SearchRequest}.
     * @param searchRequest - The search request
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an axios {@link CancelToken} if provided
     * @returns The search response.
     */
    searchAsync(searchRequest: SearchRequest, cancelCallback?: CancelCallback): Promise<SearchResponse>;

    /**
     * Send a {@link AllFacetsRequest}.
     * @param allFacetsRequest - The all facets search request
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an axios {@link CancelToken} if provided
     * @returns The search response.
     */
    allFacetsAsync(
        allFacetsRequest: AllFacetsRequest,
        cancelCallback?: CancelCallback
    ): Promise<AllFacetValuesResponse>;
}

export class SearchClient implements ISearchClient {
    private readonly _client: IExtendedContentHubClient;

    constructor(client: IExtendedContentHubClient) {
        Guard.notNullOrUndefined(client);
        this._client = client;
    }

    async searchAsync(searchRequest: SearchRequest, cancelCallback?: CancelCallback): Promise<SearchResponse> {
        Guard.notNullOrUndefined(searchRequest);

        const searchLink = await this._client.linkHelper.searchLinkAsync();

        const response = await this._client.raw.postAsync(
            searchLink.href,
            TaJson.serialize(searchRequest),
            undefined,
            cancelCallback
        );

        ResponseHandler.handleErrors(response);

        const searchResponse = TaJson.deserialize<SearchResponse>(response.content, SearchResponse);

        return searchResponse;
    }

    async allFacetsAsync(
        allFacetsRequest: AllFacetsRequest,
        cancelCallback?: CancelCallback
    ): Promise<AllFacetValuesResponse> {
        Guard.notNullOrUndefined(allFacetsRequest);

        const allFacetsLink = await this._client.linkHelper.allFacetsAsync();

        const response = await this._client.raw.postAsync(
            allFacetsLink.href,
            TaJson.serialize(allFacetsRequest),
            undefined,
            cancelCallback
        );

        ResponseHandler.handleErrors(response);

        const allFacetsResponse = TaJson.deserialize<AllFacetValuesResponse>(response.content, AllFacetValuesResponse);

        return allFacetsResponse;
    }
}
